<template>
  <div class="brand-form-view flex flex-col">
    <div v-if="isLoading" class="flex justify-center py-20">
      <loader-circle-icon class="size-8 animate-spin text-primary" />
    </div>

    <template v-else>
      <page-header :title="isNew ? tr('New Brand') : tr('Edit Brand')">
        <template #buttons>
          <ui-button variant="outline" as-child>
            <router-link :to="cancelLink">{{ tr('Cancel') }}</router-link>
          </ui-button>
          <ui-button :disabled="isSaving" @click="() => save()">
            <loader-circle-icon v-if="isSaving" class="animate-spin" />
            {{ tr('Save') }}
          </ui-button>
        </template>
      </page-header>

      <div class="flex max-w-2xl flex-col gap-5 py-5">
        <form-section
          :title="tr('Name your brand')"
          :description="tr('This will only be visible to you, so you can easily find it later. Feel free to name it however you like!')"
        >
          <ui-input v-model="form.name" :placeholder="tr('Brand name')" :aria-label="tr('Brand name')" />
        </form-section>

        <form-section
          :title="tr('Content Source')"
          :description="tr('Select the Facebook page to use as content source. The connected Instagram account will be linked automatically.')"
        >
          <template #actions>
            <refresh-button
              v-if="isNew"
              :label="tr('Update content sources')"
              :success-message="tr('Content sources updated successfully.')"
              :disabled="isRefreshing"
              :promise="() => refreshContentSources()"
            />
          </template>

          <ui-combobox-select
            v-model="form.facebook_page_external_id"
            :options="facebookPageSourceOptions"
            option-value="id"
            option-label="name"
            :placeholder="tr('Select content source')"
            :search-placeholder="tr('Search') + '...'"
            :empty-text="tr('No results')"
            :disabled="!isNew || isRefreshingContentSource"
            class="h-auto min-h-9 py-1.5"
          >
            <template #option="{ option }">
              <img :src="option.picture" alt="" class="size-6 shrink-0 rounded-full object-cover">
              <span class="truncate">{{ option.name }}</span>
              <template v-if="option.instagram_name">
                <img :src="option.instagram_picture" alt="" class="size-6 shrink-0 rounded-full object-cover">
                <span class="truncate text-muted-foreground">{{ option.instagram_name }}</span>
              </template>
            </template>
          </ui-combobox-select>

          <asset-status-message :source-profile="loadedBrand?.facebook_source_profile" />
        </form-section>

        <form-section :title="tr('Website')">
          <ui-input
            v-model="form.website"
            type="url"
            :placeholder="tr('https://example.com')"
            :aria-label="tr('Website')"
            @blur="onWebsiteInputBlur"
          />
        </form-section>

        <form-section :title="tr('Meta Ad Account')" :description="tr('Select the Meta ad account for this brand.')">
          <template #actions>
            <refresh-button
              v-if="isNew"
              :label="tr('Update ad accounts')"
              :success-message="tr('Ad accounts updated successfully.')"
              :disabled="isRefreshing"
              :promise="() => refreshAdAccounts()"
            />
          </template>

          <ui-combobox-select
            v-model="form.meta_ad_account_external_id"
            :options="metaAdAccountOptions"
            option-value="id"
            option-label="name"
            :placeholder="tr('Search ad accounts')"
            :search-placeholder="tr('Search') + '...'"
            :empty-text="tr('No results')"
            :disabled="!isNew || isRefreshingAdAccount"
          >
            <template #option="{ option, label }">
              <span :class="[ 'truncate', adAccountStatusIsUnsettled(option) && 'text-muted-foreground' ]">{{ label }}</span>
              <triangle-alert-icon
                v-if="adAccountStatusIsUnsettled(option)"
                v-tooltip="adAccountStatusText(option).text"
                class="size-4 shrink-0 text-warning"
              />
            </template>
          </ui-combobox-select>

          <asset-status-message :ad-account="selectedMetaAdAccount" :show-retry="isNew" />

          <template v-if="form.meta_ad_account_external_id">
            <label class="flex cursor-pointer items-center gap-2.5 text-sm text-foreground">
              <ui-checkbox v-model="useMetaVerifiedAdvertiser" />
              {{ tr('Use a verified advertiser on the ad sets') }}
              <info-popup>
                {{ tr('Meta can require a verified advertiser on the new ad sets of an ad account. It is taken over automatically from the ad sets created in Ads Manager, so it only needs to be given here in rare cases.') }}
              </info-popup>
            </label>

            <div v-if="useMetaVerifiedAdvertiser" class="flex flex-col gap-4 border-l-2 border-border pl-4">
              <div class="flex flex-col gap-1.5">
                <ui-label for="brand-beneficiary" class="gap-1.5">
                  {{ tr('Beneficiary ID') }}
                  <info-popup>
                    {{ tr('This ID is usually not visible on Meta\'s interface. The easiest way is to leave it to the system: in Ads Manager, create an ad set on this ad account, select the verified advertiser as its beneficiary and payer, and publish it (it can stay paused). The system takes the verified advertiser over from it automatically on its next run, so this option does not need to be turned on.') }}
                  </info-popup>
                </ui-label>
                <ui-input id="brand-beneficiary" v-model="form.meta_verified_advertiser_beneficiary_id" inputmode="numeric" />
              </div>

              <label class="flex cursor-pointer items-center gap-2.5 text-sm text-foreground">
                <ui-checkbox v-model="differentMetaVerifiedAdvertiserPayer" />
                {{ tr('A different person or organization is paying') }}
              </label>

              <div v-if="differentMetaVerifiedAdvertiserPayer" class="flex flex-col gap-1.5">
                <ui-label for="brand-payer">{{ tr('Payer ID') }}</ui-label>
                <ui-input id="brand-payer" v-model="form.meta_verified_advertiser_payer_id" inputmode="numeric" />
              </div>
            </div>
          </template>
        </form-section>

        <form-section
          :title="tr('Weekly report')"
          :description="tr('A clear, easy-to-read weekly performance report for your campaigns, delivered automatically. Includes key metrics, trends, and personalized tips to help you see what\'s working and where you can improve.')"
        >
          <template #actions>
            <ui-switch v-model="form.send_weekly_report" :aria-label="tr('Weekly report')" />
          </template>

          <template v-if="form.send_weekly_report">
            <div class="flex flex-col gap-1.5">
              <ui-label>{{ tr('Report day') }}</ui-label>
              <ui-select v-model="weeklyReportDay">
                <ui-select-trigger class="w-full sm:w-60" :aria-label="tr('Report day')">
                  <ui-select-value />
                </ui-select-trigger>
                <ui-select-content>
                  <ui-select-item v-for="day in weekDays" :key="day.value" :value="String(day.value)">
                    {{ day.label }}
                  </ui-select-item>
                </ui-select-content>
              </ui-select>
            </div>

            <div class="flex flex-col gap-1.5">
              <ui-label>{{ tr('Send report to') }}</ui-label>
              <multi-select
                v-model="form.weekly_report_to"
                :placeholder="tr('Search member')"
                :options="companyMemberOptions"
                :loading="userListIsLoading"
                :disabled="userListIsLoading"
                value-key="id"
                label-key="fullname"
              />
            </div>

            <div class="flex flex-col gap-1.5">
              <ui-label>{{ tr('Additional email addresses') }}</ui-label>
              <multi-text-input
                ref="weeklyReportEmailInput"
                v-model="form.weekly_report_emails"
                :placeholder="tr('name@example.com')"
                :add-button-label="tr('Add email address')"
                :validation-rule="isValidEmail"
              />
            </div>
          </template>
        </form-section>

        <form-section
          v-if="form.targeting"
          :title="tr('Target audience')"
          :description="tr('If you cannot define an option exactly, leave it unchanged.')"
        >
          <div class="flex flex-col gap-1.5">
            <ui-label>{{ tr('Gender') }}</ui-label>
            <gender-selector v-model="form.targeting.genders" />
          </div>

          <div class="flex flex-col gap-1.5">
            <ui-label>{{ tr('Age') }}</ui-label>
            <div class="flex items-center gap-2.5">
              <age-selector v-model="form.targeting.age_min" />
              –
              <age-selector v-model="form.targeting.age_max" />
            </div>
          </div>

          <div class="flex flex-col gap-1.5">
            <ui-label>{{ tr('Geo locations') }}</ui-label>
            <geolocation-target-selector
              ref="geolocationTargetSelector"
              v-model="form.targeting"
              :geolocations-required="false"
            />
          </div>

          <div class="flex flex-col gap-1.5">
            <ui-label>{{ tr('Language') }}</ui-label>
            <multi-select
              v-model="form.targeting.locales"
              :options="facebookLanguages"
              value-key="id"
              label-key="name"
              :placeholder="tr('Choose at least one language...')"
            />
          </div>
        </form-section>
      </div>
    </template>
  </div>
</template>

<script>
import { LoaderCircle, TriangleAlert } from '@lucide/vue';

import FacebookTargeting from '@/descriptors/FacebookTargeting.js';
import { PaymentRequiredError } from '@/errors';
import AssetStatusMixin from '@/mixins/AssetStatus.js';
import CompanyMemberListMixin from '@/mixins/CompanyMemberList.js';
import ExtraBrandChargeMixin from '@/mixins/ExtraBrandCharge.js';
import BrandService from '@/services/Brand.js';
import ConnectedProfileService from '@/services/ConnectedProfile.js';
import AgeSelector from '@/ui/components/AgeSelector.vue';
import AssetStatusMessage from '@/ui/components/AssetStatusMessage.vue';
import FormSection from '@/ui/components/FormSection.vue';
import GenderSelector from '@/ui/components/GenderSelector.vue';
import GeolocationTargetSelector from '@/ui/components/GeolocationTargetSelector.vue';
import InfoPopup from '@/ui/components/InfoPopup.vue';
import MultiSelect from '@/ui/components/MultiSelect.vue';
import MultiTextInput from '@/ui/components/MultiTextInput.vue';
import PageHeader from '@/ui/components/PageHeader.vue';
import RefreshButton from '@/ui/components/RefreshButton.vue';
import { isValidEmail, isValidURL } from '@/utils/validators.js';

const REFRESH_TARGET_CONTENT_SOURCE = 'content-source';
const REFRESH_TARGET_AD_ACCOUNT = 'ad-account';

export default {
  name: 'BrandFormView',
  components: {
    AgeSelector,
    AssetStatusMessage,
    FormSection,
    GenderSelector,
    GeolocationTargetSelector,
    InfoPopup,
    LoaderCircleIcon: LoaderCircle,
    MultiSelect,
    MultiTextInput,
    RefreshButton,
    PageHeader,
    TriangleAlertIcon: TriangleAlert,
  },
  mixins: [ AssetStatusMixin, CompanyMemberListMixin, ExtraBrandChargeMixin ],
  beforeRouteEnter(to, from, next) {
    next(vm => vm.redirectIfMissingPlanForNewBrand());
  },
  data: () => ({
    isLoading: false,
    isSaving: false,
    loadedBrand: null,
    refreshingSelect: null,
    useMetaVerifiedAdvertiser: false,
    differentMetaVerifiedAdvertiserPayer: false,
    form: {
      name: '',
      meta_ad_account_external_id: null,
      facebook_page_external_id: null,
      send_weekly_report: false,
      weekly_report_day: 2,
      weekly_report_to: [],
      weekly_report_emails: [],
      website: '',
      targeting: new FacebookTargeting(),
      meta_verified_advertiser_beneficiary_id: '',
      meta_verified_advertiser_payer_id: '',
    },
  }),
  computed: {
    facebookLanguages() {
      const list = JSON.parse(JSON.stringify(this.$store.getters['app/facebookLanguages']));
      list.sort((a, b) => a.name.localeCompare(b.name));

      return list;
    },
    isNew() {
      return !this.$route.params.id;
    },
    isRefreshing() {
      return this.refreshingSelect !== null;
    },
    isRefreshingContentSource() {
      return this.refreshingSelect === REFRESH_TARGET_CONTENT_SOURCE;
    },
    isRefreshingAdAccount() {
      return this.refreshingSelect === REFRESH_TARGET_AD_ACCOUNT;
    },
    cancelLink() {
      return this.isNew ? '/brands' : '/brands/' + this.$route.params.id;
    },
    metaAdAccountOptions() {
      if (!this.isNew && this.loadedBrand?.meta_ad_account) {
        const aa = this.loadedBrand.meta_ad_account;
        return [ {
          id: aa.external_id,
          external_id: aa.external_id,
          name: aa.name,
          status: aa.status,
          platform: aa.platform,
          has_payment_method: aa.has_payment_method,
          lost_access: aa.lost_access,
          tos_accepted: aa.tos_accepted,
          deleted_at: aa.deleted_at,
        } ];
      }

      const PLATFORM = 'facebook';

      const options = [];
      const processedExternalIds = [];

      const connectedProfiles = (this.$root.loggedInUser.connected_profiles || []);
      connectedProfiles
        .filter(p => p.platform === PLATFORM)
        .forEach(profile => {
          (profile.details?.ad_accounts || []).forEach(aa => {
            if (processedExternalIds.includes(aa.id)) {
              return;
            }
            processedExternalIds.push(aa.id);
            options.push({
              id: aa.id,
              external_id: aa.id,
              name: aa.name,
              status: aa.status,
              platform: PLATFORM,
              has_payment_method: aa.has_payment_method,
              lost_access: aa.lost_access,
              tos_accepted: aa.tos_accepted,
              deleted_at: aa.deleted_at,
            });
          });
        });

      options.sort((a, b) => a.name.localeCompare(b.name));

      return options;
    },
    selectedMetaAdAccount() {
      if (!this.form.meta_ad_account_external_id) {
        return null;
      }
      return this.metaAdAccountOptions.find(o => o.id === this.form.meta_ad_account_external_id) || null;
    },
    facebookPageSourceOptions() {
      if (!this.isNew && this.loadedBrand?.facebook_source_profile) {
        return [ {
          id: this.loadedBrand.facebook_source_profile.external_id,
          name: this.loadedBrand.facebook_source_profile.name,
          picture: this.loadedBrand.facebook_source_profile.picture,
          website: this.loadedBrand.facebook_source_profile.website,
          instagram_name: this.loadedBrand.instagram_source_profile?.name,
          instagram_picture: this.loadedBrand.instagram_source_profile?.picture,
        } ];
      }

      const PLATFORM = 'facebook';

      const options = [];
      const processedExternalIds = [];

      const connectedProfiles = (this.$root.loggedInUser.connected_profiles || []);
      connectedProfiles
        .filter(p => p.platform === PLATFORM)
        .forEach(profile => {
          (profile.details?.pages || []).forEach(page => {
            if (processedExternalIds.includes(page.id)) {
              return;
            }

            processedExternalIds.push(page.id);
            options.push({
              id: page.id,
              name: page.name,
              picture: page.picture,
              website: page.website || '',
              instagram_name: page.instagram_business_account?.name || '',
              instagram_picture: page.instagram_business_account?.picture || '',
            });
          });
        });

      options.sort((a, b) => a.name.localeCompare(b.name));

      return options;
    },
    // The kit select works with string values.
    weeklyReportDay: {
      get() {
        return String(this.form.weekly_report_day);
      },
      set(value) {
        this.form.weekly_report_day = Number(value);
      },
    },
    weekDays() {
      return [
        { value: 2, label: this.tr('Monday') },
        { value: 3, label: this.tr('Tuesday') },
        { value: 4, label: this.tr('Wednesday') },
        { value: 5, label: this.tr('Thursday') },
        { value: 6, label: this.tr('Friday') },
        { value: 7, label: this.tr('Saturday') },
        { value: 1, label: this.tr('Sunday') },
      ];
    },
  },
  watch: {
    // Brands saved without a weekly report have no day yet: start from Monday,
    // like a new brand.
    'form.send_weekly_report'(enabled) {
      if (enabled && !this.form.weekly_report_day) {
        this.form.weekly_report_day = 2;
      }
    },
    'form.facebook_page_external_id'(externalId) {
      if (externalId) {
        const selectedPage = this.facebookPageSourceOptions.find(o => o.id === externalId);
        if (selectedPage) {
          this.form.website = selectedPage.website;
        }
      }
    },
  },
  created() {
    if (!this.isNew) {
      this.loadEntity();
    }
  },
  methods: {
    redirectIfMissingPlanForNewBrand() {
      if (!this.isNew || this.$root.currentCompany?.current_subscription?.package) {
        return;
      }

      this.$toast.clear();
      this.$toast.error(this.tr('No active plan found. Please subscribe to a plan before creating new brands.'));
      this.$router.replace('/brands');
    },
    loadEntity() {
      this.isLoading = true;
      BrandService.get(this.$route.params.id)
        .then(brand => {
          this.loadedBrand = brand;

          this.form.name = brand.name;
          this.form.meta_ad_account_external_id = brand.meta_ad_account?.external_id;
          this.form.facebook_page_external_id = brand.facebook_source_profile?.external_id;
          this.form.send_weekly_report = brand.send_weekly_report;
          this.form.weekly_report_day = brand.weekly_report_day;
          this.form.weekly_report_to = brand.weekly_report_to.map(u => u.id);
          this.form.weekly_report_emails = brand.weekly_report_emails;
          this.form.website = brand.website;
          this.form.targeting = new FacebookTargeting(brand.targeting || {});
          this.form.meta_verified_advertiser_beneficiary_id = brand.meta_verified_advertiser_beneficiary_id || '';
          this.form.meta_verified_advertiser_payer_id = brand.meta_verified_advertiser_payer_id || '';
          this.useMetaVerifiedAdvertiser = !!this.form.meta_verified_advertiser_beneficiary_id;
          this.differentMetaVerifiedAdvertiserPayer = !!this.form.meta_verified_advertiser_payer_id
            && this.form.meta_verified_advertiser_payer_id !== this.form.meta_verified_advertiser_beneficiary_id;

          return brand;
        })
        .catch(err => this.error(err))
        .finally(() => this.isLoading = false);
    },
    refreshContentSources() {
      return this.refreshConnectedProfiles(REFRESH_TARGET_CONTENT_SOURCE);
    },
    refreshAdAccounts() {
      return this.refreshConnectedProfiles(REFRESH_TARGET_AD_ACCOUNT);
    },
    refreshConnectedProfiles(target) {
      this.refreshingSelect = target;

      return ConnectedProfileService.refreshAll()
        .then(() => this.$store.dispatch('auth/fetch'))
        .then(resp => {
          this.clearUnavailableSelections();

          return resp;
        })
        .finally(() => this.refreshingSelect = null);
    },
    clearUnavailableSelections() {
      if (
        this.form.facebook_page_external_id
        && !this.facebookPageSourceOptions.some(o => o.id === this.form.facebook_page_external_id)
      ) {
        this.form.facebook_page_external_id = null;
      }

      if (
        this.form.meta_ad_account_external_id
        && !this.metaAdAccountOptions.some(o => o.id === this.form.meta_ad_account_external_id)
      ) {
        this.form.meta_ad_account_external_id = null;
      }
    },
    isValidEmail,
    validate() {
      this.$toast.clear();

      if (!this.form.name.trim()) {
        this.error(this.tr('Brand name is required.'));
        return false;
      }

      if (!this.form.facebook_page_external_id) {
        this.error(this.tr('The {field} field cannot be empty!', { '{field}': this.tr('Content Source') }));
        return false;
      }

      if (this.form.website && !isValidURL(this.form.website)) {
        this.error(this.tr('Invalid website URL.'));
        return false;
      }

      if (!this.form.meta_ad_account_external_id) {
        this.error(this.tr('The {field} field cannot be empty!', { '{field}': this.tr('Meta Ad Account') }));
        return false;
      }

      if (this.form.send_weekly_report) {
        const hasUserRecipients = this.form.weekly_report_to.length > 0;
        if (!hasUserRecipients && this.form.weekly_report_emails.length === 0) {
          this.error(this.tr('Please select at least one weekly report recipient (user or email).'));
          return false;
        }

        if (this.$refs.weeklyReportEmailInput && !this.$refs.weeklyReportEmailInput.validate()) {
          this.error(this.tr('Please enter valid email addresses for the weekly report.'));
          return false;
        }
      }

      if (this.$refs.geolocationTargetSelector && !this.$refs.geolocationTargetSelector.validate()) {
        return false;
      }

      if (this.useMetaVerifiedAdvertiser) {
        const ids = [ this.form.meta_verified_advertiser_beneficiary_id ];
        if (!ids[0]) {
          this.error(this.tr('The {field} field cannot be empty!', { '{field}': this.tr('Beneficiary ID') }));
          return false;
        }

        if (this.differentMetaVerifiedAdvertiserPayer) {
          ids.push(this.form.meta_verified_advertiser_payer_id);
          if (!ids[1]) {
            this.error(this.tr('The {field} field cannot be empty!', { '{field}': this.tr('Payer ID') }));
            return false;
          }
        }

        if (ids.some(id => !/^\d+$/.test(id))) {
          this.error(this.tr('The ID of the verified advertiser can only contain digits.'));
          return false;
        }
      }

      return true;
    },
    getSaveData(chargeExtraBrands) {
      const data = { ...this.form };
      data.targeting = data.targeting.toObject();
      if (!this.useMetaVerifiedAdvertiser) {
        data.meta_verified_advertiser_beneficiary_id = null;
      }
      if (!this.useMetaVerifiedAdvertiser || !this.differentMetaVerifiedAdvertiserPayer) {
        data.meta_verified_advertiser_payer_id = null;
      }

      if (this.isNew) {
        data.charge_extra_brands = chargeExtraBrands;
      }

      return data;
    },
    openNewBrandChargeConfirmation(err) {
      this.openExtraBrandChargeConfirmation(err, {
        title: this.tr('Creating new brand'),
        intro: this.tr(
          'You are attempting to create a new brand, but your current subscription allows a maximum of :maxNumber brands.',
          { ':maxNumber': this.$root.currentCompany.max_brands },
        ),
        confirm: () => this.save(true),
      });
    },
    async save(chargeExtraBrands = false) {
      if (!this.validate()) {
        return;
      }

      this.isSaving = true;

      try {
        const data = this.getSaveData(chargeExtraBrands);

        let brand;
        if (this.isNew) {
          brand = await BrandService.create(data);
          this.$toast.success(this.tr('Brand created successfully.'));
        }
        else {
          brand = await BrandService.update(this.$route.params.id, data);
          this.$toast.success(this.tr('Brand updated successfully.'));
        }

        await this.$store.dispatch('auth/fetch');
        this.$router.push('/brands/' + brand.id);
      }
      catch (err) {
        if (this.isNew && !chargeExtraBrands && err instanceof PaymentRequiredError) {
          this.openNewBrandChargeConfirmation(err);
          return;
        }

        this.error(err);
      }
      finally {
        this.isSaving = false;
      }
    },
    onWebsiteInputBlur() {
      if (
        this.form.website
        && isValidURL(this.form.website)
        && this.form.website.toLowerCase().indexOf('http') !== 0
      ) {
        this.form.website = 'https://' + this.form.website;
      }
    },
  },
};
</script>
